import React, { useCallback, useEffect, useState } from "react";
import { Sparkles, RefreshCw, Loader2, ChevronDown } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";

const fmt = (iso) => (iso ? new Date(iso).toLocaleString("fr-FR", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }) : "");

export default function AiInsight({ scope, refId = "all", title = "Synthèse IA", className = "", testid }) {
  const { user } = useAuth() || {};
  const storeKey = `norea_ai_collapsed:${user?.email || "anon"}:${scope.split(":")[0]}`;
  const [collapsed, setCollapsed] = useState(() => localStorage.getItem(storeKey) === "1");
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const tid = testid || `ai-insight-${scope.replace(":", "-")}`;
  const url = `/ai-insights/${encodeURIComponent(scope)}?ref=${encodeURIComponent(refId)}`;

  const generate = useCallback(async (silent) => {
    setBusy(true);
    try {
      const { data: d } = await api.post(url);
      setData(d);
      if (!silent) toast.success("Synthèse IA mise à jour");
    } catch (e) {
      if (!silent) toast.error(e?.response?.data?.detail || "Synthèse IA indisponible");
    } finally {
      setBusy(false);
    }
  }, [url]);

  useEffect(() => {
    let alive = true;
    setData(null);
    api.get(url).then(({ data: d }) => {
      if (!alive) return;
      setData(d);
      const old = !d.generated_at || Date.now() - new Date(d.generated_at).getTime() > 10 * 60 * 1000;
      const local = scope.startsWith("tab:") || scope === "subcontractor";
      if (!d.lines || (d.stale && (local || old))) generate(true);
    }).catch(() => {});
    return () => { alive = false; };
  }, [url, generate, scope]);

  const toggle = () => {
    localStorage.setItem(storeKey, collapsed ? "0" : "1");
    setCollapsed(!collapsed);
  };

  return (
    <div className={`rounded-lg border border-emerald-200 bg-emerald-50/80 px-5 ${collapsed ? "py-2.5" : "py-4"} ${className}`} data-testid={tid}>
      <div className="flex items-center justify-between gap-3">
        <button onClick={toggle} className="flex items-center gap-2 text-emerald-900 min-w-0" data-testid={`${tid}-toggle`} title={collapsed ? "Afficher" : "Réduire"}>
          <ChevronDown className={`w-4 h-4 transition-transform ${collapsed ? "-rotate-90" : ""}`} />
          <Sparkles className="w-4 h-4" />
          <span className="text-[11px] uppercase tracking-wider font-semibold">{title}</span>
          {data?.generated_at && <span className="text-[11px] text-emerald-800/70 truncate">· mise à jour {fmt(data.generated_at)}</span>}
          {busy && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
        </button>
        {!collapsed && (
          <button onClick={() => generate(false)} disabled={busy} className="text-xs px-2.5 py-1 rounded border border-emerald-300 bg-white/70 text-emerald-900 hover:bg-white inline-flex items-center gap-1.5 disabled:opacity-60" data-testid={`${tid}-regenerate`}>
            <RefreshCw className="w-3.5 h-3.5" /> Régénérer
          </button>
        )}
      </div>
      {!collapsed && (
        !data?.lines ? (
          <div className="mt-3 text-sm text-emerald-900/80 inline-flex items-center gap-2" data-testid={`${tid}-loading`}>
            <Loader2 className="w-4 h-4 animate-spin" /> Analyse en cours…
          </div>
        ) : (
          <div className="mt-3 grid grid-cols-1 md:grid-cols-3 gap-4">
            {data.labels.map((label, i) => (
              <div key={label} className="min-w-0" data-testid={`${tid}-line-${i}`}>
                <div className="text-[10px] uppercase tracking-wider text-emerald-800/80 mb-1">{label}</div>
                <div className={`text-sm leading-snug ${i === 2 ? "font-medium text-emerald-950" : "text-emerald-950/90"}`}>{data.lines[i] || "—"}</div>
              </div>
            ))}
          </div>
        )
      )}
    </div>
  );
}
